import { useMemo, useState } from 'react';
import { Calendar, Check, ChevronDown, FolderKanban, Search } from 'lucide-react';
import {
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@helios/ui';
import { useProjectScopeOptions } from '@/features/projects/projects.hooks';
import { useGlobalFilters, type DateRangeValue } from '@/hooks/useGlobalFilters';

function DateRangeFilter() {
  const { range, setRange, label, isFiltered, options } = useGlobalFilters();
  const [open, setOpen] = useState(false);

  const select = (value: DateRangeValue) => {
    setRange(value);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={'Date range: ' + label}
          className={cn(
            'backdrop-blur-glass focus-visible:ring-ring inline-flex h-7 items-center gap-1.5 rounded-full border px-2 py-0 text-xs font-medium leading-4 shadow-inner transition-colors focus-visible:outline-none focus-visible:ring-2',
            isFiltered
              ? 'border-primary/50 bg-primary/15 text-foreground'
              : 'text-muted-foreground hover:text-foreground border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.08]',
          )}
        >
          <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">{label}</span>
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1">
        <p className="text-2xs text-muted-foreground px-2 py-1.5 font-semibold uppercase">
          Date range
        </p>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => select(option.value)}
            className="text-foreground hover:bg-primary/15 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm transition-colors"
          >
            {option.label}
            {option.value === range ? (
              <Check className="text-primary h-3.5 w-3.5" aria-hidden="true" />
            ) : null}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

interface ScopeOption {
  id: string;
  label: string;
  detail?: string;
}

function ScopeFilter({
  label,
  icon: Icon,
  value,
  options,
  loading,
  error,
  searchable,
  onChange,
}: {
  label: string;
  icon: typeof FolderKanban;
  value: string;
  options: ScopeOption[];
  loading: boolean;
  error: boolean;
  searchable?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selected = options.find((option) => option.id === value);
  const filtered = searchable
    ? options.filter((option) =>
        (option.label + ' ' + (option.detail ?? ''))
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      )
    : options;
  const buttonLabel = selected?.label ?? (value ? 'Selected project' : label);
  const unavailable = error || (!loading && options.length === 0);
  const reason = error
    ? 'Project scope is temporarily unavailable.'
    : 'No project scope options are available.';

  const button = (
    <button
      type="button"
      aria-label={buttonLabel}
      aria-disabled={unavailable}
      onClick={() => {
        if (!unavailable) setOpen(true);
      }}
      className={cn(
        'backdrop-blur-glass focus-visible:ring-ring inline-flex h-7 max-w-44 items-center gap-1.5 rounded-full border px-2 py-0 text-xs font-medium leading-4 shadow-inner transition-colors focus-visible:outline-none focus-visible:ring-2',
        value
          ? 'border-primary/50 bg-primary/15 text-foreground'
          : 'text-muted-foreground hover:text-foreground border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.08]',
        unavailable && 'cursor-not-allowed opacity-55',
      )}
    >
      {loading ? (
        <Spinner className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      )}
      <span className="hidden truncate lg:inline">{buttonLabel}</span>
      <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
    </button>
  );

  if (unavailable) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="bottom">{reason}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>{button}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-1">
        <p className="text-2xs text-muted-foreground px-2 py-1.5 font-semibold uppercase">
          {label}
        </p>
        {searchable ? (
          <label className="mb-1 block px-1">
            <span className="sr-only">Search project scopes</span>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search projects"
              startAdornment={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
              className="border-border/70 focus-within:border-border-strong text-xs focus-within:ring-0 focus-within:ring-offset-0"
            />
          </label>
        ) : null}
        <ScrollArea className="max-h-72">
          <button
            type="button"
            onClick={() => {
              onChange('');
              setOpen(false);
            }}
            className="text-foreground hover:bg-primary/15 flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm"
          >
            {label}
            {!value ? <Check className="text-primary h-3.5 w-3.5" aria-hidden="true" /> : null}
          </button>
          {filtered.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                onChange(option.id);
                setOpen(false);
              }}
              className="hover:bg-primary/15 flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2 text-left"
            >
              <span className="min-w-0">
                <span className="text-foreground block truncate text-sm">{option.label}</span>
                {option.detail ? (
                  <span className="text-muted-foreground block truncate text-xs">
                    {option.detail}
                  </span>
                ) : null}
              </span>
              {option.id === value ? (
                <Check className="text-primary h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              ) : null}
            </button>
          ))}
          {filtered.length === 0 ? (
            <p className="text-muted-foreground px-3 py-6 text-center text-xs">
              No matching projects
            </p>
          ) : null}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

export function FilterBar() {
  const { projectId, setScopes } = useGlobalFilters();
  const scopes = useProjectScopeOptions();
  const projects = useMemo(() => scopes.data?.items ?? [], [scopes.data?.items]);
  const projectOptions = useMemo(
    () =>
      projects.map((project) => ({
        id: project.id,
        label: project.name,
        detail: project.projectNumber + (project.region ? ' · ' + project.region.code : ''),
      })),
    [projects],
  );

  const selectProject = (nextProjectId: string) => {
    const selectedProject = projects.find((project) => project.id === nextProjectId);
    setScopes({
      projectId: nextProjectId,
      regionId: selectedProject?.region?.id ?? '',
    });
  };

  return (
    <div className="flex items-center gap-2">
      <DateRangeFilter />
      <ScopeFilter
        icon={FolderKanban}
        label="All projects"
        value={projectId}
        options={projectOptions}
        loading={scopes.isLoading}
        error={scopes.isError}
        searchable
        onChange={selectProject}
      />
    </div>
  );
}
